import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ImageThumbnail } from "shared/visuals/image-thumbnail";
import { useDownloadDocument } from "shared/editors/hooks/use-download-document";
import { formatTimestamp } from "utils/formats";
import type { ThumbnailEntry } from "../hooks/use-extra-work-agreement-thumbnails";
import { ImageViewerDialog } from "./imageViewerDialog";

interface Props {
  entry: ThumbnailEntry;
  size?: number | string;
  canDelete: boolean;
  onDelete?: (entry: ThumbnailEntry) => void;
}

const triggerBlobDownload = (blobUrl: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = fileName;
  a.click();
};

export const ThumbnailFrame = ({ entry, size = 256, canDelete, onDelete }: Props) => {
  const { t } = useTranslation();
  const downloadDocument = useDownloadDocument();
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const isSavedDocument = !!entry.documentId && !entry.documentId.startsWith("temp-");
  const canDownload = isSavedDocument || !!entry.pendingFile;

  const handleOpenViewer = () => setIsViewerOpen(true);
  const handleCloseViewer = () => setIsViewerOpen(false);

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isSavedDocument && entry.documentId) {
      void downloadDocument({ documentId: entry.documentId, name: entry.altText });
      return;
    }
    if (entry.pendingFile) {
      triggerBlobDownload(entry.imageUrl, entry.altText);
    }
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onDelete?.(entry);
  };

  const formattedDate = entry.uploadedTimestamp
    ? formatTimestamp(entry.uploadedTimestamp, false, "short")
    : "";

  const isNumericSize = typeof size === "number";
  const showActions = canDownload || canDelete;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: size,
        "&:hover .thumbnail-frame-actions": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: isNumericSize ? size : "auto",
          aspectRatio: isNumericSize ? undefined : "1 / 1",
        }}
      >
        <ImageThumbnail
          imageUrl={entry.imageUrl}
          altText={entry.altText}
          size="100%"
          onClick={handleOpenViewer}
        />
        {showActions && (
          <Stack
            direction="row"
            spacing={0.5}
            className="thumbnail-frame-actions"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "opacity 0.15s ease-in-out",
            }}
          >
            {canDownload && (
              <Tooltip title={t("dashboard.extraWorkAgreements.create.image.download")}>
                <IconButton
                  size="small"
                  onClick={handleDownloadClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <FileDownloadOutlinedIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t("common.delete")}>
                <IconButton
                  size="small"
                  onClick={handleDeleteClick}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.85)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                  }}
                >
                  <DeleteOutlinedIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ pt: 0.5, px: 0.25, width: size }}>
        <Typography
          variant="body2"
          color="primary.main"
          title={entry.altText}
          sx={{
            fontWeight: 500,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {entry.altText}
        </Typography>
        {formattedDate && (
          <Typography variant="caption" color="grey.50" sx={{ display: "block" }}>
            {formattedDate}
          </Typography>
        )}
      </Box>
      <ImageViewerDialog
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        imageUrl={entry.imageUrl}
        fileName={entry.altText}
        documentId={isSavedDocument ? entry.documentId : undefined}
      />
    </Box>
  );
};
